import React, { useCallback, useEffect, useState } from 'react';
// Components
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyContent,
  Flex,
  Progress,
  StatusChip,
  Text,
} from '@/components/ui';
import {
  IconAlertTriangle,
  IconCheck,
  IconDatabase,
  IconFileImport,
  IconLoader,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';

// API services
import {
  createImportMapping,
  ImportMappingResponse,
} from '@/services/importMappingsApi';

// Utils
import {
  transformWizardStateToApiRequest,
  validateMappingData,
  ValidationResult,
} from '../../utils/mappingTransformer';

// Types
import { StepComponentProps } from '../types';

type ImportStepProps = StepComponentProps;

interface ImportResult {
  status:
    | 'pending'
    | 'processing'
    | 'creating_mapping'
    | 'mapping_created'
    | 'completed'
    | 'failed';
  progress: number;
  currentFile?: string;
  currentStep?: string;
  sitesCreated: number;
  subjectsCreated: number;
  formsProcessed: number;
  recordsImported: number;
  totalRecords: number;
  estimatedTimeRemaining?: number;
  errors?: ImportError[];
  mappingId?: number; // ID of created import mapping
}

interface ImportError {
  fileId: string;
  message: string;
  details?: string;
  row?: number;
  column?: string;
}

export const ImportStep: React.FC<ImportStepProps> = ({
  wizardState,
  onNext,
  canGoNext,
  currentStep,
}) => {
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const startImport = useCallback(async () => {
    setIsImporting(true);

    // Initialize import result
    const initialResult: ImportResult = {
      status: 'pending',
      progress: 0,
      sitesCreated: 0,
      subjectsCreated: 0,
      formsProcessed: 0,
      recordsImported: 0,
      totalRecords: 0,
      errors: [],
    };

    setImportResult(initialResult);

    try {
      // Step 1: Validate mapping data
      setImportResult(prev =>
        prev
          ? {
              ...prev,
              status: 'processing',
              progress: 10,
              currentStep: 'Validating mapping data...',
            }
          : null
      );

      const validation: ValidationResult = validateMappingData(wizardState);
      if (!validation.isValid) {
        const errorMessages = validation.errors.map(
          err => `${err.message}${err.details ? `: ${err.details}` : ''}`
        );
        throw new Error(
          `Mapping has validation warnings:\n${errorMessages.join('\n')}`
        );
      }

      // Step 2: Transform wizard state to API format
      setImportResult(prev =>
        prev
          ? {
              ...prev,
              progress: 25,
              currentStep: 'Preparing mapping data...',
            }
          : null
      );

      const apiRequestData = transformWizardStateToApiRequest(wizardState);

      // Step 3: Create import mapping via API
      setImportResult(prev =>
        prev
          ? {
              ...prev,
              status: 'creating_mapping',
              progress: 50,
              currentStep: 'Creating import mapping...',
            }
          : null
      );

      const mappingResponse: ImportMappingResponse =
        await createImportMapping(apiRequestData);

      // Step 4: Mapping created successfully
      setImportResult(prev =>
        prev
          ? {
              ...prev,
              status: 'mapping_created',
              progress: 75,
              currentStep: 'Import mapping created successfully',
              mappingId: mappingResponse.id,
              formsProcessed: wizardState.fileToFormMappings.length,
            }
          : null
      );

      // Step 5: Simulate actual import process (for now, since import execution API doesn't exist yet)
      setImportResult(prev =>
        prev
          ? {
              ...prev,
              status: 'processing',
              progress: 90,
              currentStep: 'Executing import process...',
            }
          : null
      );

      // Simulate some processing time
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 6: Complete the import
      const finalResult: ImportResult = {
        status: 'completed',
        progress: 100,
        currentStep: 'Import completed successfully',
        mappingId: mappingResponse.id,
        sitesCreated: 0, // Will be populated when actual import API is implemented
        subjectsCreated: 0,
        formsProcessed: wizardState.fileToFormMappings.length,
        recordsImported: 0, // Will be populated when actual import API is implemented
        totalRecords: 0, // Will be populated when actual import API is implemented
        errors: [], // No errors for mapping creation
      };

      setImportResult(finalResult);
    } catch (error) {
      setImportResult({
        status: 'failed',
        progress: 0,
        sitesCreated: 0,
        subjectsCreated: 0,
        formsProcessed: 0,
        recordsImported: 0,
        totalRecords: 0,
        errors: [
          {
            fileId: '',
            message: 'Import mapping creation failed',
            details:
              error instanceof Error
                ? error.message
                : 'Unknown error occurred while creating import mapping',
          },
        ],
      });
    } finally {
      setIsImporting(false);
    }
  }, [wizardState]);

  // Auto-start import when component mounts
  useEffect(() => {
    if (!importResult && !isImporting) {
      startImport();
    }
  }, [importResult, isImporting, startImport]);

  const handleRestartImport = useCallback(() => {
    setImportResult(null);
    setIsImporting(false);
  }, []);

  const getStatusColor = (status: ImportResult['status']) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'processing':
      case 'creating_mapping':
        return 'info';
      case 'mapping_created':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Flex direction="column" gap="4">
      {/* Header */}
      <Card>
        <Flex justify="between" align="center">
          <Flex direction="column" gap="1">
            <Text size="5" weight="bold">
              Step {currentStep}: Import Data
            </Text>
            <Text size="3" color="gray">
              Execute the data import process
            </Text>
          </Flex>
          {importResult && (
            <StatusChip
              entityType="import"
              status={
                importResult.status === 'creating_mapping' ||
                importResult.status === 'mapping_created'
                  ? 'processing'
                  : importResult.status === 'completed'
                    ? 'completed'
                    : importResult.status
              }
              variant="soft"
              size="small"
              label={
                importResult.status === 'pending'
                  ? 'Preparing...'
                  : importResult.status === 'processing'
                    ? 'Importing...'
                    : importResult.status === 'creating_mapping'
                      ? 'Creating Mapping...'
                      : importResult.status === 'mapping_created'
                        ? 'Mapping Created'
                        : importResult.status === 'completed'
                          ? 'Import Complete'
                          : importResult.status === 'failed'
                            ? 'Import Failed'
                            : undefined
              }
            />
          )}
        </Flex>
      </Card>

      {/* Import Progress */}
      {!importResult ? (
        <Card>
          <EmptyContent
            title="Starting import process..."
            description="Please wait while we prepare your data for import."
            icon={<IconLoader className="animate-spin" />}
          />
        </Card>
      ) : (
        <Flex direction="column" gap="4">
          {/* Progress Bar */}
          <Card>
            <Flex direction="column" gap="3">
              <Flex justify="between" align="center">
                <Text size="4" weight="medium">
                  Import Progress
                </Text>
                <Text size="3" color="gray">
                  {importResult.progress}%
                </Text>
              </Flex>

              <Progress
                value={importResult.progress}
                max={100}
                size="small"
                color={getStatusColor(importResult.status)}
              />

              {importResult.currentStep && (
                <Text size="2" color="gray" align="center">
                  {importResult.currentStep}
                </Text>
              )}

              {importResult.estimatedTimeRemaining &&
                importResult.status === 'processing' && (
                  <Text size="1" color="gray" align="center">
                    Estimated time remaining:{' '}
                    {Math.ceil(importResult.estimatedTimeRemaining / 60)}{' '}
                    minutes
                  </Text>
                )}
            </Flex>
          </Card>

          {/* Import Statistics */}
          <Flex gap="4">
            <Card style={{ flex: 1 }}>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <IconDatabase color="var(--blue-9)" />
                  <Text size="3" weight="medium">
                    Data Summary
                  </Text>
                </Flex>

                <Flex direction="column" gap="2">
                  <Flex justify="between">
                    <Text size="2">Sites Created:</Text>
                    <Badge color="info" variant="soft">
                      {importResult.sitesCreated}
                    </Badge>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Subjects Created:</Text>
                    <Badge color="success" variant="soft">
                      {importResult.subjectsCreated}
                    </Badge>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Forms Processed:</Text>
                    <Badge color="warning" variant="soft">
                      {importResult.formsProcessed}
                    </Badge>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Records Imported:</Text>
                    <Badge color="feature" variant="soft">
                      {importResult.recordsImported} /{' '}
                      {importResult.totalRecords}
                    </Badge>
                  </Flex>
                </Flex>
              </Flex>
            </Card>

            <Card style={{ flex: 1 }}>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <IconFileImport color="var(--green-9)" />
                  <Text size="3" weight="medium">
                    Import Details
                  </Text>
                </Flex>

                <Flex direction="column" gap="2">
                  <Flex justify="between">
                    <Text size="2">Files Processed:</Text>
                    <Badge color="info" variant="soft">
                      {wizardState.selectedFiles.length}
                    </Badge>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Forms Mapped:</Text>
                    <Badge color="success" variant="soft">
                      {wizardState.fileToFormMappings.length}
                    </Badge>
                  </Flex>
                  {importResult.mappingId && (
                    <Flex justify="between">
                      <Text size="2">Mapping ID:</Text>
                      <Badge color="feature" variant="soft">
                        #{importResult.mappingId}
                      </Badge>
                    </Flex>
                  )}
                  <Flex justify="between">
                    <Text size="2">Columns Mapped:</Text>
                    <Badge color="warning" variant="soft">
                      {wizardState.columnMappings.length}
                    </Badge>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Success Rate:</Text>
                    <Badge
                      color={
                        importResult.status === 'completed'
                          ? 'success'
                          : 'error'
                      }
                      variant="soft"
                    >
                      {importResult.status === 'completed' ? '100%' : '0%'}
                    </Badge>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          </Flex>

          {/* Errors and Warnings */}
          {importResult.errors && importResult.errors.length > 0 && (
            <Card>
              <Flex direction="column" gap="3">
                <Flex align="center" gap="2">
                  <IconAlertTriangle color="var(--orange-9)" />
                  <Text size="4" weight="medium">
                    Import Issues
                  </Text>
                </Flex>

                <Flex direction="column" gap="2">
                  {importResult.errors.map((error, index) => (
                    <Alert key={index} color="warning">
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          {error.message}
                        </Text>
                        {error.details && (
                          <Text size="2" color="gray">
                            {error.details}
                          </Text>
                        )}
                        {(error.row || error.column) && (
                          <Text size="1" color="gray">
                            {error.row && `Row: ${error.row}`}
                            {error.row && error.column && ' • '}
                            {error.column && `Column: ${error.column}`}
                          </Text>
                        )}
                      </Flex>
                    </Alert>
                  ))}
                </Flex>
              </Flex>
            </Card>
          )}

          {/* Success Message */}
          {importResult.status === 'completed' && (
            <Card>
              <Alert color="success">
                <IconCheck />
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Import completed successfully!
                  </Text>
                  <Text size="2">
                    All data has been imported into the EDC system. You can now
                    view the imported data in your study forms.
                  </Text>
                </Flex>
              </Alert>
            </Card>
          )}

          {/* Failed Message */}
          {importResult.status === 'failed' && (
            <Card>
              <Alert color="error">
                <IconX />
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Import failed
                  </Text>
                  <Text size="2">
                    The import process encountered errors and could not
                    complete. Please check the error details above and try
                    again.
                  </Text>
                </Flex>
              </Alert>
            </Card>
          )}
        </Flex>
      )}

      {/* Navigation */}
      <Card>
        <Flex justify="center">
          <Flex gap="2">
            {importResult?.status === 'failed' && (
              <Button
                variant="soft"
                color="orange"
                onClick={handleRestartImport}
                disabled={isImporting}
              >
                <IconRefresh />
                Retry Import
              </Button>
            )}

            {importResult?.status === 'completed' && (
              <Button
                variant="soft"
                color="green"
                onClick={() => {
                  // Navigate to study overview or imported data
                }}
              >
                View Imported Data
              </Button>
            )}

            <Button
              variant="classic"
              color="blue"
              onClick={onNext}
              disabled={!canGoNext || importResult?.status !== 'completed'}
            >
              Finish Import
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
};
